import React, { useState, useEffect } from 'react';
import { X, Search, Users, UserCheck, GraduationCap } from 'lucide-react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useToast } from '../../contexts/ToastContext';
import { Staff, Teacher, Student } from '../../types';

interface MemberSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (member: any) => void;
    type: 'staff' | 'teacher' | 'student';
}


const MemberSelectionModal: React.FC<MemberSelectionModalProps> = ({
    isOpen,
    onClose,
    onSelect,
    type
}) => {
    const { showError } = useToast();
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [members, setMembers] = useState<(Staff | Teacher | Student)[]>([]);

    useEffect(() => {
        if (isOpen) {
            fetchMembers();
        }
    }, [isOpen, type]);

    const fetchMembers = async () => {
        setLoading(true);
        try {
            let membersData: (Staff | Teacher | Student)[] = [];

            switch (type) {
                case 'staff':
                    membersData = await fetchStaff();
                    break;
                case 'teacher':
                    membersData = await fetchTeachers();
                    break;
                case 'student':
                    membersData = await fetchStudents();
                    break;
            }

            setMembers(membersData);
        } catch (error) {
            console.error('Error fetching members:', error);
            showError('Error', 'Failed to fetch members');
        } finally {
            setLoading(false);
        }
    };

    const fetchStaff = async (): Promise<Staff[]> => {
        try {
            const staffRef = collection(db, 'staff');
            const q = query(staffRef, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dateOfBirth: doc.data().dateOfBirth?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
                updatedAt: doc.data().updatedAt?.toDate(),
                currentPosition: {
                    ...doc.data().currentPosition,
                    joiningDate: doc.data().currentPosition?.joiningDate?.toDate(),
                },
            })) as Staff[];
        } catch (error) {
            console.error('Error fetching staff:', error);
            return [];
        }
    };

    const fetchTeachers = async (): Promise<Teacher[]> => {
        try {
            const teachersRef = collection(db, 'teachers');
            const q = query(teachersRef, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dateOfBirth: doc.data().dateOfBirth?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
                updatedAt: doc.data().updatedAt?.toDate(),
                currentPosition: {
                    ...doc.data().currentPosition,
                    joiningDate: doc.data().currentPosition?.joiningDate?.toDate(),
                },
            })) as Teacher[];
        } catch (error) {
            console.error('Error fetching teachers:', error);
            return [];
        }
    };

    const fetchStudents = async (): Promise<Student[]> => {
        try {
            const studentsRef = collection(db, 'students');
            const q = query(studentsRef, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dateOfBirth: doc.data().dateOfBirth?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
                updatedAt: doc.data().updatedAt?.toDate(),
            })) as Student[];
        } catch (error) {
            console.error('Error fetching students:', error);
            return [];
        }
    };

    const filteredMembers = members.filter(member => {
        const searchLower = searchTerm.toLowerCase();
        return (
            member.firstName.toLowerCase().includes(searchLower) ||
            member.lastName.toLowerCase().includes(searchLower) ||
            (member as any).staffId?.toLowerCase().includes(searchLower) ||
            (member as any).teacherId?.toLowerCase().includes(searchLower) ||
            (member as any).studentId?.toLowerCase().includes(searchLower) ||
            (member as any).rollNumber?.toLowerCase().includes(searchLower)
        );
    });

    const getMemberIcon = () => {
        switch (type) {
            case 'staff':
                return <Users className="h-5 w-5 text-blue-600" />;
            case 'teacher':
                return <UserCheck className="h-5 w-5 text-purple-600" />;
            case 'student':
                return <GraduationCap className="h-5 w-5 text-emerald-600" />;
        }
    };

    const getMemberTypeColor = () => {
        switch (type) {
            case 'staff':
                return 'bg-blue-100 text-blue-800';
            case 'teacher':
                return 'bg-purple-100 text-purple-800';
            case 'student':
                return 'bg-emerald-100 text-emerald-800';
        }
    };

    const getMemberId = (member: any) => {
        switch (type) {
            case 'staff':
                return (member as Staff).staffId;
            case 'teacher':
                return (member as Teacher).teacherId;
            case 'student':
                return (member as Student).studentId;
        }
    };

    const getMemberDetails = (member: any) => {
        switch (type) {
            case 'staff':
                return {
                    id: (member as Staff).staffId,
                    position: (member as Staff).currentPosition?.designation || 'Staff'
                };
            case 'teacher':
                return {
                    id: (member as Teacher).teacherId,
                    position: (member as Teacher).currentPosition?.designation || 'Teacher'
                };
            case 'student':
                return {
                    id: (member as Student).studentId,
                    position: `${(member as Student).academicInfo.currentGrade} - ${(member as Student).academicInfo.section}`
                };
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl shadow-xl max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        {getMemberIcon()}
                        <h2 className="text-lg font-semibold text-gray-900">
                            Select {type.charAt(0).toUpperCase() + type.slice(1)}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Search */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder={`Search ${type}s...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                    </div>
                </div>

                {/* Members List */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                                <p className="text-sm text-gray-600">Loading {type}s...</p>
                            </div>
                        </div>
                    ) : filteredMembers.length > 0 ? (
                        <div className="p-4 space-y-2">
                            {filteredMembers.map((member) => {
                                const memberDetails = getMemberDetails(member);
                                const memberId = getMemberId(member);

                                return (
                                    <div
                                        key={member.id}
                                        onClick={() => {
                                            onSelect({
                                                ...member,
                                                type: type
                                            });
                                            onClose();
                                        }}
                                        className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                                                    <span className="text-white font-semibold text-sm">
                                                        {member.firstName.charAt(0)}{member.lastName.charAt(0)}
                                                    </span>
                                                </div>
                                                <div>
                                                    <h4 className="font-medium text-gray-900">
                                                        {member.firstName} {member.lastName}
                                                    </h4>
                                                    <p className="text-sm text-gray-600">
                                                        {memberDetails.id}
                                                    </p>
                                                    <p className="text-sm text-gray-500">
                                                        {memberDetails.position}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMemberTypeColor()}`}>
                                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                                </span>
                                                {type === 'student' && (
                                                    <span className="text-xs text-gray-500">
                                                        Roll: {(member as Student).academicInfo.rollNumber}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    {getMemberIcon()}
                                </div>
                                <p className="text-gray-500">No {type}s found</p>
                                {searchTerm && (
                                    <p className="text-sm text-gray-400 mt-1">
                                        Try adjusting your search terms
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        {filteredMembers.length} {type}s found
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MemberSelectionModal;
